import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { marked } from 'marked';
import { useDispatch, useSelector } from 'react-redux';

// Redux Slices
import { updateCard } from '../../redux/Slices/cardSlice';
import { fetchActivities, setActivitiesFromSocket } from '../../redux/Slices/activitiesSlice';
import { fetchTimer } from '../../redux/Slices/timerSlice';
import { addMember, addMemberOptimistic, setCardMembersFromSocket } from '../../redux/Slices/memberSlice';
import { setCardLabelsFromSocket } from '../../redux/Slices/labelSlice';
import { setChecklistsFromSocket } from '../../redux/Slices/checklistSlice';
import { setAttachmentsFromSocket } from '../../redux/Slices/attachmentSlice';

// Utils and Config
import { TIMER_STATUS_COLOR, getTextColor, hideTooltips, textTransform } from '../../utils/helpers';
import { config } from '../../config';
import { refreshAttachmentUrl, isRefreshableAttachmentUrl } from '../../utils/attachmentUtils';


// Components
import CardMembers from './CardMembers';
import CardLabels from './CardLabels';
import Attachments from './Attachments';
import Checklist from './Checklists';
import Comments from './Comments';
import Activities from './Activities';
import CardDetailsSidebar from './CardDetailsSidebar';
import Editor from '../LexicalEditor/Editor';
import Checkbox from '../Global/Checkbox';
import Confetti from '../Global/Confetti';
import LinkPreviewWrapper from '../Global/LinkPreviewWrapper';
import AccessDenied from '../AccessDenied';
import '../Global/scss/ImageSquareLoader.scss';


// Icons
import Notifications from '../icons/Notifications';
import Check from '../icons/Check';
import Timer from '../icons/Timer';
import HourglassTop from '../icons/HourglassTop';
import EditNote from '../icons/EditNote';
import ActivityZone from '../icons/ActivityZone';
import Brightness1 from '../icons/Brightness1';
import CircleCheck from '../icons/CircleCheck';
import AttachFile from '../icons/AtachFile';
import Add from '../icons/Add';
import NotificationsActive from '../icons/NotificationsActive';
import MoreHoriz from '../icons/MoreHoriz';
import IconChecklist from '../icons/IconChecklist';
import Group from '../icons/Group';
import PriorityFlag from '../icons/PriorityFlag';
import PersonAdd from '../icons/PersonAdd';
import Close from '../icons/Close';

// Hooks
import useConfetti from '../../hooks/useConfetti';
import { useTimer } from '../../hooks/useTimer';
import { useLightbox, Lightbox } from '../../hooks/useLightbox';
import { useSocketAction } from '../../hooks/useSocketManager';
import { ActionTypes } from '../../sockets/ActionTypes';
import Campaign from '../icons/Campaign';
import ImagesMode from '../icons/ImagesMode';
import Share from '../icons/Share';
import KeyboardArrowDown from '../icons/KeyboardArrowDown';


const CardDetailsFullLayout = ({
    card,
    cardId,
    boardId,
    title,
    setTitle,
    description,
    setDescription,
    isEditingDescription,
    setIsEditingDescription,
    isTextareaFocused,
    setIsTextareaFocused,
    // isExpanded,
    // setIsExpanded,
    // isOverflowing,
    // contentRef,
    textAreaRef,
    handleUpdateCard,
    updateCardDetails,
    actionListTitle,
    handlePopoverClick,
    isWatching,
    handleWatch,
    isChecked,
    setIsChecked,
    attachments,
    setBoard,
    coverColor,
    handleCloseCardPopup,
    layoutStyle,
    setLayoutStyle,
    isReadOnly = false
}) => {
    const dispatch = useDispatch();
    const [sidebarTab, setSidebarTab] = useState('both'); // 'comments', 'activities', 'both'
    const [isCompleted, setIsCompleted] = useState(card?.completed || false);
    const [isCompletionAnimating, setIsCompletionAnimating] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);

    const cardHeaderRef = useRef(null);
    const { isActive: confettiActive, triggerConfetti, handleComplete: handleConfettiComplete } = useConfetti();
    const [isHeaderSticky, setIsHeaderSticky] = useState(false);
    const [archived, setArchived] = useState(card.archived);
    const [showDetails, setShowDetails] = useState(card?.isActivityDetails); // State to track if details are shown
    const [isJoining, setIsJoining] = useState(false);
    const [error, setError] = useState(null);

    // Lightbox hook for image viewing
    const { images, currentIndex, isOpen, closeLightbox, nextImage, prevImage } = useLightbox('.card-description-content');
    const { user } = useSelector((state) => state.user);
    const { cardMembers } = useSelector(state => state.member);
    const memberIds = cardMembers?.map(member => member?._id);
    const isUserMember = user && user.user ? memberIds?.includes(user?.user?._id) : false;

    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [measured, setMeasured] = useState(false);
    const contentRef = useRef(null);

    const updateCardData = { ...card, users: cardMembers };

    useEffect(() => {
        if (contentRef.current) {
            const actualHeight = contentRef.current.scrollHeight;
            setIsOverflowing(actualHeight > 200);
            setMeasured(true);
        }
    }, [description, contentRef.current]);
    // Auto-refresh expired images inside the description (markdown-rendered)
    useEffect(() => {
        if (!contentRef.current) return;
        const container = contentRef.current;

        const imgs = Array.from(container.querySelectorAll('img'));
        const handlers = new Map();

        imgs.forEach((img) => {
            const onError = async () => {
                // Limit retry attempts to prevent infinite loops
                const MAX_RETRY_ATTEMPTS = 5;
                const currentAttempts = parseInt(img.dataset.refreshAttempts || '0', 10);

                if (currentAttempts >= MAX_RETRY_ATTEMPTS) return;

                img.dataset.refreshAttempts = String(currentAttempts + 1);

                // Measure current size to avoid layout shift
                const rect = img.getBoundingClientRect();
                const minW = Math.max(120, Math.floor(rect.width || 0));
                const minH = Math.max(90, Math.floor(rect.height || 0));

                // Create loader placeholder: centered stylish square box
                const loader = document.createElement('div');
                loader.className = 'image-loading-placeholder image-refetch-loader';
                loader.style.minWidth = `${minW}px`;
                loader.style.minHeight = `${minH}px`;
                loader.style.width = rect.width ? `${Math.floor(rect.width)}px` : '';
                loader.style.height = rect.height ? `${Math.floor(rect.height)}px` : '';
                loader.innerHTML = '<div class="image-loading-square"></div>';

                // Hide broken image and insert loader next to it
                img.style.visibility = 'hidden';
                img.after(loader);

                try {
                    if (isRefreshableAttachmentUrl(img.src)) {
                        const newUrl = await refreshAttachmentUrl(img.src);
                        if (newUrl) {
                            img.src = newUrl;
                        }
                    }
                } catch (err) {
                    // no-op
                } finally {
                    // Restore image visibility and remove loader regardless of outcome
                    loader.remove();
                    img.style.visibility = '';
                }
            };

            handlers.set(img, onError);
            img.addEventListener('error', onError);
        });

        return () => {
            handlers.forEach((handler, img) => img.removeEventListener('error', handler));
        };
    }, [description]);


    // Measure header height for sticky positioning
    useEffect(() => {
        const measureHeaderHeight = () => {
            if (cardHeaderRef.current) {
                const height = cardHeaderRef.current.offsetHeight;
                setHeaderHeight(height);
            }
        };

        measureHeaderHeight();

        // Re-measure on window resize
        window.addEventListener('resize', measureHeaderHeight);

        return () => {
            window.removeEventListener('resize', measureHeaderHeight);
        };
    }, [card, isHeaderSticky]); // Re-measure when card data changes or sticky state changes




    const mainContentRef = useRef(null);
    const timer = useTimer(cardId);

    // Fetch timer data when component mounts
    useEffect(() => {
        dispatch(fetchTimer({ cardId }));
    }, [cardId, dispatch]);

    // Real-time socket listener for card updates
    useSocketAction([
        ActionTypes.CARD_UPDATED,
        ActionTypes.MEMBER_ADDED,
        ActionTypes.MEMBER_REMOVED,
        ActionTypes.LABEL_ADDED,
        ActionTypes.LABEL_DELETED,
        ActionTypes.DUEDATE_ADDED,
        ActionTypes.DUEDATE_UPDATED,
        ActionTypes.DUEDATE_REMOVED,
        ActionTypes.PRIORITY_UPDATED,
        ActionTypes.CHECKLIST_ADDED,
        ActionTypes.CHECKLIST_UPDATED,
        ActionTypes.CHECKLIST_DELETED,
        ActionTypes.CHECKLIST_ITEM_ADDED,
        ActionTypes.CHECKLIST_ITEM_UPDATED,
        ActionTypes.CHECKLIST_ITEM_DELETED,
        ActionTypes.ATTACHMENT_ADDED,
        ActionTypes.ATTACHMENT_UPDATED,
        ActionTypes.ATTACHMENT_DELETED,
        ActionTypes.COMMENT_ADDED,
        ActionTypes.COMMENT_UPDATED,
        ActionTypes.COMMENT_DELETED,
        ActionTypes.COMMENT_REACTION_TOGGLED,
        ActionTypes.REPLY_ADDED,
        ActionTypes.REPLY_UPDATED,
        ActionTypes.REPLY_DELETED,
        ActionTypes.REPLY_REACTION_TOGGLED,
    ], (data) => {
        // Skip events triggered by current user (already handled optimistically)
        if (data.reqUser === user?.user?._id) return;

        // Only process events for this specific card
        // Card events have a 'card' property with the card object
        if (!data.card || data.card.shortId !== cardId) return;

        console.log('[CardDetailsFullLayout] Real-time event:', data.action, 'for card:', cardId, 'data:', data);

        // Update Redux state optimistically with data from socket
        // The backend sends populated card data, so we can directly update Redux
        switch (data.action) {
            case ActionTypes.MEMBER_ADDED:
            case ActionTypes.MEMBER_REMOVED:
                // Update card members with populated data from socket
                if (data.card.users) {
                    dispatch(setCardMembersFromSocket(data.card.users));
                }
                break;
            case ActionTypes.LABEL_ADDED:
            case ActionTypes.LABEL_UPDATED:
            case ActionTypes.LABEL_DELETED:
                // Update card labels with data from socket
                if (data.card.labels) {
                    dispatch(setCardLabelsFromSocket({ cardId, labels: data.card.labels }));
                }
                break;
            case ActionTypes.CHECKLIST_ADDED:
            case ActionTypes.CHECKLIST_UPDATED:
            case ActionTypes.CHECKLIST_DELETED:
            case ActionTypes.CHECKLIST_ITEM_ADDED:
            case ActionTypes.CHECKLIST_ITEM_UPDATED:
            case ActionTypes.CHECKLIST_ITEM_DELETED:
                // Update checklists with data from socket
                if (data.card.checklists) {
                    dispatch(setChecklistsFromSocket(data.card.checklists));
                }
                break;
            case ActionTypes.ATTACHMENT_ADDED:
            case ActionTypes.ATTACHMENT_UPDATED:
            case ActionTypes.ATTACHMENT_DELETED:
                // Update attachments with data from socket
                if (data.card.attachments) {
                    dispatch(setAttachmentsFromSocket(data.card.attachments));
                }
                break;
            case ActionTypes.COMMENT_ADDED:
            case ActionTypes.COMMENT_UPDATED:
            case ActionTypes.COMMENT_DELETED:
            case ActionTypes.COMMENT_REACTION_TOGGLED:
            case ActionTypes.REPLY_ADDED:
            case ActionTypes.REPLY_UPDATED:
            case ActionTypes.REPLY_DELETED:
            case ActionTypes.REPLY_REACTION_TOGGLED:
                // ✅ Refetch activities/comments to get updated list
                // Note: Comments are complex with replies, reactions, etc.
                // Fetching is more reliable than trying to update optimistically
                dispatch(fetchActivities({ cardId, isActivityDetails: false }));
                break;
            default:
                break;
        }

        // Update the card in the board context
        setBoard((prevBoard) => {
            if (!prevBoard || !prevBoard.actionLists) return prevBoard;

            return {
                ...prevBoard,
                actionLists: prevBoard.actionLists.map((actionList) => ({
                    ...actionList,
                    cards: actionList.cards.map((currentCard) => {
                        if (currentCard.shortId !== cardId) return currentCard;

                        // Merge the updated card data
                        const updatedCard = {
                            ...currentCard,
                            ...data.card,
                        };

                        // Update local state based on the event type
                        switch (data.action) {
                            case ActionTypes.CARD_UPDATED:
                                // Update title if changed
                                if (data.card.title && data.card.title !== title) {
                                    setTitle(data.card.title);
                                }
                                // Update description if changed
                                if (data.card.description !== undefined && data.card.description !== description) {
                                    setDescription(data.card.description);
                                }
                                // Update completed state if changed
                                if (data.card.completed !== undefined && data.card.completed !== isCompleted) {
                                    setIsCompleted(data.card.completed);
                                    if (setIsChecked) {
                                        setIsChecked(data.card.completed);
                                    }
                                }
                                break;
                            default:
                                break;
                        }

                        return updatedCard;
                    }),
                })),
            };
        });
    }, [cardId, user?.user?._id, setBoard, title, setTitle, description, setDescription, isCompleted, setIsChecked, dispatch]);

    const handleCardCompletion = async () => {
        const newCompletedState = !isCompleted;

        try {
            setIsCompletionAnimating(true);

            // Optimistic update
            setIsCompleted(newCompletedState);
            if (setIsChecked) {
                setIsChecked(newCompletedState);
            }

            // Trigger confetti for completion (not for uncompleting)
            if (newCompletedState) {
                triggerConfetti();
            }

            // Update card completion via Redux
            const completedData = { completed: newCompletedState };

            dispatch(updateCard({ cardId, updatedData: completedData }));

            // Animation timeout
            setTimeout(() => {
                setIsCompletionAnimating(false);
            }, 600);

        } catch (error) {
            console.error('Error updating card completion:', error);
            // Revert optimistic update on error
            setIsCompleted(!isCompleted);
            if (setIsChecked) {
                setIsChecked(!newCompletedState);
            }
            setIsCompletionAnimating(false);
        }
    };

    const handleJoinToCard = async (e, member) => {
        e.preventDefault();
        setIsJoining(true);
        const memberId = member.user._id;

        // Optimistic update
        dispatch(addMemberOptimistic({ cardId, member: member.user }));

        try {
            await dispatch(addMember({ cardId, memberId })).unwrap();
            // dispatch(fetchActivities({ cardId }));
        } catch (error) {
            setError('Failed to add member.');
        } finally {
            setIsJoining(false);
        }
    };

    const handleArchiveCard = async (archivedStatus) => {
        setArchived(archivedStatus); // Optimistically update local state


        try {
            const response = await fetch(config.API_URI + `/v1/cards/archive/${cardId}`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({ archived: archivedStatus }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.message || 'Failed to archive the card';

                // Rollback optimistic update
                setArchived(!archivedStatus);

                // Show appropriate error message
                if (response.status === 403) {
                    alert(errorMessage || 'You do not have permission to archive this card');
                } else {
                    alert(errorMessage);
                }
                return;
            }

            const data = await response.json();

            setBoard((prevBoard) => ({
                ...prevBoard,
                actionLists: prevBoard.actionLists.map((list) => {
                    if (list._id === data.updatedCard.actionList._id) {
                        const updatedCard = data.updatedCard;

                        const newCards = archivedStatus
                            ? list.cards.filter((card) => card.shortId !== cardId)
                            : [...list.cards];

                        if (!archivedStatus) {
                            const insertIndex = newCards.findIndex((card) => card.order > updatedCard.order);
                            if (insertIndex === -1) {
                                newCards.push(updatedCard);
                            } else {
                                newCards.splice(insertIndex, 0, updatedCard);
                            }
                        }

                        return {
                            ...list,
                            cards: newCards.sort((a, b) => a.order - b.order),
                        };
                    }
                    return list;
                }),
            }));


        } catch (error) {
            setArchived(!archivedStatus); // Revert UI on error
            console.error('Error archiving the card:', error);
        }
    };

    // Scroll detection for sticky header
    useEffect(() => {
        const handleScroll = () => {
            if (mainContentRef.current) {
                const scrollTop = mainContentRef.current.scrollTop;
                setIsHeaderSticky(scrollTop > 50);
            }
        };

        const mainContent = mainContentRef.current;
        if (mainContent) {
            mainContent.addEventListener('scroll', handleScroll);
            return () => mainContent.removeEventListener('scroll', handleScroll);
        }
    }, []);

    const handleActivities = () => {
        setShowDetails((prevState) => !prevState); // Toggle show/hide
        dispatch(fetchActivities({ cardId, isActivityDetails: !showDetails })); // Fetch based on toggle state
    };


    let notificationsColor = 'var(--brand-color)';
    let headrBorderColor = 'var(--popover-border-color)';
    if (card?.cover?.url) {
        notificationsColor = getTextColor(coverColor[0], true);
        headrBorderColor = 'transparent';
    }


    return (
        <div className="card-details-full">

            {/* Main Content Area */}
            <div className="card-main-content" ref={mainContentRef}>
                {/* Header Actions */}
                <div className="card-header-actions-bar" style={{ borderColor: headrBorderColor }}>

                    {/* Notifications */}
                    <div
                        className="card-action-btn"
                        id='popover-notifications'
                        onClick={handleWatch}
                        data-popover-trigger
                        data-tooltip-content={isWatching ? 'Notifying' : 'Notify'}
                        data-tooltip-position="top"
                    >
                        {
                            isWatching ? (
                                <NotificationsActive
                                    color={'var(--primary-text-color)'}
                                    size={18}
                                />
                            ) : (
                                <Notifications
                                    color={'var(--primary-text-color)'}
                                    size={18}
                                />
                            )
                        }
                    </div>

                    {/* Share Card */}
                    <div
                        className="card-action-btn"
                        id='popover-share-card'
                        onClick={(e) => handlePopoverClick(e, 'shareCard', { cardId })}
                        data-popover-trigger
                        data-tooltip-content="Share card"
                        data-tooltip-position="top"
                    >

                        <Share
                            color='var(--primary-text-color)'
                            size={18}
                        />

                    </div>

                    {/* Cover icon */}
                    {!isReadOnly && (
                        <div
                            className="card-action-btn"
                            id='popover-card-cover'
                            onClick={(e) => handlePopoverClick(e, 'attachment', {
                                type: 'COVER'
                            })}
                            data-popover-trigger
                            data-tooltip-content="Cover"
                            data-tooltip-position="top"

                        >
                            <ImagesMode
                                color='var(--primary-text-color)'
                                size={20}
                            />
                        </div>
                    )}

                    {/* More Actions */}
                    <div
                        className="card-action-btn"
                        id='popover-more-actions'
                        onClick={(e) => handlePopoverClick(e, 'cardActions', { cardId, card: updateCardData, isReadOnly })}
                        data-popover-trigger
                        data-tooltip-content="More actions"
                        data-tooltip-position="top"
                    >
                        <MoreHoriz
                            color='var(--primary-text-color)'
                            size={18}
                        />

                    </div>


                    {/* Close Button */}
                    <div className='card-action-btn close-card' onClick={handleCloseCardPopup} title="Close">
                        <Close
                            color='var(--primary-text-color)'
                            size={18}
                        />
                    </div>
                </div>

                {/* Cover Image */}
                {card?.cover?.url && (
                    <div className="card-cover-image" style={{ backgroundColor: coverColor[0] }}>
                        <img src={card?.cover?.url} alt={card.cover.name} loading="lazy" />
                    </div>
                )}

                {/* Header Section */}
                <div className="card-header" ref={cardHeaderRef}>
                    <div className="card-title-section">

                        {isHeaderSticky && !isReadOnly && (
                            <div className="card-header-actions">
                                <button
                                    className="card-sticky-add-btn"
                                    id='popover-card-sticky-add'
                                    onClick={(e) => handlePopoverClick(e, 'addToCardActions', { cardId, card })}
                                    data-popover-trigger
                                    title="Add to card"
                                >
                                    <span className="material-symbols-outlined">add</span>
                                </button>
                            </div>
                        )}

                        <div className="card-title">
                            <span
                                className={`card-complete-icon ${isCompleted ? 'completed' : ''} ${(isCompletionAnimating && isCompleted) ? 'animating' : ''}`}
                                onClick={handleCardCompletion}
                                data-tooltip-content={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                                data-tooltip-position="top"
                                data-popover-trigger
                            >
                                {isCompleted ? (
                                    <CircleCheck size={22} color='var(--secondary-brand-color)' />
                                ) : (
                                    <Brightness1 size={22} />
                                )}
                            </span>
                            {isTextareaFocused ? (
                                <textarea
                                    ref={textAreaRef}
                                    className="card-title-input"
                                    value={title}
                                    onChange={(e) => {
                                        setTitle(e.target.value);
                                        updateCardDetails(cardId, { title: e.target.value });
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleUpdateCard();
                                            setIsTextareaFocused(false);
                                        }
                                    }}
                                    onBlur={() => {
                                        handleUpdateCard();
                                        setIsTextareaFocused(false);
                                    }}
                                    spellCheck={isTextareaFocused}
                                />
                            ) : (
                                <h1
                                    className="card-title"
                                    onClick={() => !isReadOnly && setIsTextareaFocused(true)}
                                    style={{ cursor: isReadOnly ? 'default' : 'text' }}
                                // ref={textAreaRef}
                                >
                                    {title}
                                </h1>
                            )}
                        </div>

                        <div className="card-list-info">
                            in list <span
                                className="card-list-name"
                                id='popover-movecard'
                                onClick={(e) => !isReadOnly && handlePopoverClick(e, 'moveCard', { cardId, card })}
                                data-popover-trigger
                            >{actionListTitle}</span>
                        </div>
                    </div>
                </div>

                {/* Quick Actions Bar - Important Actions Only */}
                <div className="card-quick-actions">


                    {
                        !isReadOnly && (
                            <button
                                className="card-quick-action card-add-action"
                                id='popover-card-actions-popover'
                                onClick={(e) => handlePopoverClick(e, 'addToCardActions', { cardId, card })}
                                data-popover-trigger
                            >
                                <Add size={22} />
                                <span>Insert</span>
                            </button>
                        )
                    }


                    <button
                        className="card-quick-action"
                        id='popover-card-timer'
                        onClick={(e) => !isReadOnly && handlePopoverClick(e, 'cardTimer', { cardId, cardTimer: timer })}
                        data-popover-trigger
                    >
                        {timer.mode == 'stopwatch' ? (
                            <Timer size={18} color={TIMER_STATUS_COLOR[timer.timerStatus]} />
                        ) : (
                            <HourglassTop size={18} color={TIMER_STATUS_COLOR[timer.timerStatus]} />
                        )}
                        <span>Timer</span>
                    </button>

                    <button
                        className="card-quick-action"
                        id='popover-card-priority'
                        onClick={(e) => !isReadOnly && handlePopoverClick(e, 'cardPriority', { cardId, isClosed: false })}
                        data-popover-trigger
                    >
                        <PriorityFlag priority={card.priority} size={18} />
                        <span>{card.priority}</span>
                    </button>

                    {
                        !isReadOnly && (
                            <button
                                className="card-quick-action"
                                id='popover-card-checklist'
                                onClick={(e) => handlePopoverClick(e, 'addChecklist', { cardId })}
                                data-popover-trigger
                            >
                                <IconChecklist size={18} />
                                <span>Checklist</span>

                            </button>
                        )
                    }

                    {/* implement join */}
                    {
                        !isReadOnly && (
                            <>
                                {!isUserMember ? (
                                    <button
                                        className="card-quick-action"
                                        id='popover-card-join'
                                        onClick={(e) => handleJoinToCard(e, user)}
                                        data-popover-trigger
                                    >
                                        <PersonAdd size={18} />
                                        <span>Join</span>
                                    </button>
                                ) : (
                                    <button
                                        className="card-quick-action"
                                        id='popover-card-members'
                                        onClick={(e) => handlePopoverClick(e, 'cardMemberesPopover', { cardId })}
                                        data-popover-trigger
                                    >
                                        <Group size={18} />
                                        <span>Members</span>

                                    </button>
                                )}
                            </>
                        )
                    }


                </div>

                {/* Metadata Section */}
                <div className="card-metadata">
                    <div className="card-metadata-row">
                        <CardMembers cardId={cardId} isReadOnly={isReadOnly} />
                        <CardLabels cardId={cardId} isReadOnly={isReadOnly} />
                        {card?.dueDate?.date && (
                            <div className="card-due-date">
                                <h5>Due Date</h5>
                                <div className='duedate-content'>
                                    <Checkbox
                                        checked={isChecked}
                                        onChange={(checked) => setIsChecked(checked)}
                                    />
                                    <div className="duedate" id='popover-duedate' onClick={(e) => handlePopoverClick(e, 'addDueDate')} data-popover-trigger>
                                        <span className="zoobbe-watching">
                                            {format(card.dueDate.date, 'MMM dd')}, {card.dueDate.dueTime}
                                        </span>
                                        {card.dueDate.status === 'Completed' && (
                                            <span className="completed-text">Completed</span>
                                        )}
                                        {card.dueDate.status === 'Overdue' && (
                                            <span className="overdue-text">Overdue</span>
                                        )}
                                        <KeyboardArrowDown />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Description Section */}
                <div className="card-description-section">
                    <div className="card-section-header" style={{ top: `${headerHeight - 1}px` }}>
                        <h3>
                            <span className="material-symbols-outlined">subject</span>
                            Description
                        </h3>

                        {!isReadOnly && (
                            !isEditingDescription ? (
                                <button className="card-edit-btn" onClick={() => setIsEditingDescription(true)}>
                                    <EditNote size={18} />
                                    Edit
                                </button>
                            ) : (
                                <div className="description-edit-actions">
                                    <button className="save-editing-content" onClick={() => { handleUpdateCard(); setIsEditingDescription(false); }} data-tooltip-content="Save" data-tooltip-position="top">
                                        <Check size={20} color='var(--brand-color)' />
                                    </button>
                                    <button className="cancel-editing-content" onClick={() => setIsEditingDescription(false)} data-tooltip-content="Cancel" data-tooltip-position="top">
                                        <Close size={18} />
                                    </button>
                                </div>
                            )
                        )}
                    </div>

                    {!description && !isEditingDescription && !isReadOnly && (
                        <div className="card-no-description" onClick={() => setIsEditingDescription(true)}>
                            Add a more detailed description...
                        </div>
                    )}

                    {!isEditingDescription && description && (
                        <div className="card-description-wrapper">
                            <LinkPreviewWrapper
                                ref={contentRef}
                                className={`card-description-content ${!(!isExpanded) ? 'expanded' : isOverflowing ? 'collapsed' : ''}`}
                                dangerouslySetInnerHTML={{ __html: marked(card.description || '') }}
                                onClick={(e) => {
                                    const anchor = e.target.closest('a');
                                    const image = e.target.closest('img');

                                    if (anchor) {
                                        window.open(anchor.href, '_blank');
                                        e.preventDefault();
                                    } else if (image) {
                                        // Let the useLightbox hook handle image clicks
                                        // Don't prevent default or stop propagation
                                        return;
                                    } else {
                                        !isReadOnly && setIsEditingDescription(true);
                                    }
                                }}
                            />
                            {!isExpanded && isOverflowing && <div onClick={() => setIsExpanded(!isExpanded)} className="card-fade-shadow" />}

                            {isOverflowing && (
                                <button
                                    className="card-see-more"
                                    onClick={() => setIsExpanded(!isExpanded)}
                                >
                                    {isExpanded ? 'See Less' : 'See More'}
                                </button>
                            )}
                        </div>
                    )}

                    {isEditingDescription && card && (
                        <div className="card-editor-wrapper">
                            <Editor
                                value={description}
                                setDescription={setDescription}
                                setIsEditingDescription={setIsEditingDescription}
                                handleUpdateCard={handleUpdateCard}
                                boardId={boardId}
                                cardId={cardId}
                                showActionButtons={false}
                                context="card_description"
                            />
                        </div>
                    )}
                </div>

                {/* Attachments */}
                {attachments?.length > 0 && (
                    <div className="card-attachments">
                        <Attachments cardId={cardId} updateCardDetails={updateCardDetails} />
                    </div>
                )}

                {/* Checklist */}
                <div className="card-checklist">
                    <Checklist cardId={cardId} isReadOnly={isReadOnly} />
                </div>

                {/* Actions Sidebar */}
                {/* <div className="facebook-actions-sidebar">
                    <CardDetailsSidebar
                        cardId={cardId}
                        boardId={boardId}
                        setBoard={setBoard}
                        isArchived={card?.archived}
                    />
                </div> */}
            </div>

            {/* Right Sidebar - Comments and Activities */}
            <div className="card-sidebar-content">

                <div className="card-section-header">
                    <h3>
                        <ActivityZone size={24} />
                        Comments & Activity
                    </h3>

                    <div className="card-sidebar-tabs">

                        <div
                            className={`card-tab`}
                            onClick={() => handleActivities()}
                            data-tooltip-content={!showDetails ? 'Show Details' : 'Hide Details'}
                            data-tooltip-position="top"
                        >
                            <span className="material-symbols-outlined">
                                {!showDetails ? 'expand_all' : 'collapse_all'}
                            </span>
                        </div>

                        {/* Layout Switcher */}
                        {/* <div
                            className="card-tab layout-switch-btn"
                            onClick={() => {
                                setLayoutStyle(layoutStyle === 'full' ? 'current' : 'full');
                                hideTooltips();

                            }}
                            data-tooltip-content={layoutStyle === 'facebook' ? 'Switch Layout' : 'Switch Layout'}
                            data-tooltip-position="top"
                        >
                            <span className="material-symbols-outlined">
                                {layoutStyle === 'facebook' ? 'view_agenda' : 'view_column_2'}
                            </span>
                        </div> */}

                        <div
                            className={`feedback-icon`}
                            data-tooltip-content="Feedback"
                            data-tooltip-position="top"
                            id={`popover-feedback-button-${cardId}`}
                            onClick={(e) => { handlePopoverClick(e, 'feedBack') }}
                            data-popover-trigger
                        >
                            <Campaign size={20} />

                        </div>

                        <div
                            className={`card-tab close-card`}
                            data-popover-trigger
                            data-tooltip-content="Close"
                            data-tooltip-position="top"
                            onClick={(e) => handleCloseCardPopup(e)}
                        >
                            <Close size={20} />
                        </div>
                    </div>
                </div>


                <div className="card-comments-section">
                    <Activities layout='collumn' cardId={cardId} isReadOnly={isReadOnly} isActivityDetails={showDetails} />
                    <Comments layout='collumn' isEditingDescription={isEditingDescription} cardId={cardId} isActivityDetails={showDetails} isReadOnly={isReadOnly} />
                </div>
            </div>

            {/* Full Viewport Confetti */}
            <Confetti
                isActive={confettiActive}
                onComplete={handleConfettiComplete}
                fullViewport={true}
            />

            {/* Lightbox for image viewing */}
            <Lightbox
                images={images}
                attachments={attachments}
                currentIndex={currentIndex}
                isOpen={isOpen}
                closeLightbox={closeLightbox}
                nextImage={nextImage}
                prevImage={prevImage}
            />
        </div>
    );
};

export default CardDetailsFullLayout;
